// core
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// ui primitives (replaced @mui/material — see Components/UI)
import {
    Button,
    Card,
    CircularProgress,
    IconButton,
    Stack,
    TextField,
    VisibilityIcon,
    VisibilityOffIcon,
} from "Components/UI";

// assets
import { faLock, faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// services
import { UseSetPassword } from "Services/LoginAPI";

// styles
import styles from "./LoginForm.module.css";
import LoginLogo from "./LoginLogo";

// main component
export default function ForgotLoginForm({ token }) {
    // injections
    const navigate = useNavigate();

    // own state
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [password1, setPassword1] = useState("");
    const [password2, setPassword2] = useState("");
    const [showPassword1, setShowPassword1] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);

    // private methods
    function DoSet(e) {
        e.preventDefault();

        setLoading(true);
        if (password1 !== password2) {
            setErrorMessage("Passwords do not match, try again.");
            setLoading(false);
            return;
        }

        UseSetPassword(token, password1)
            .then((response) => {
                if (response.status) {
                    if (response.status === true) {
                        toast.success("Password reset, please login.");
                        setTimeout(() => {
                            navigate("/");
                        }, 4000);
                    } else {
                        setErrorMessage(
                            `Password reset failed - ${response.info}`
                        );
                    }
                } else {
                    setErrorMessage(`Password reset failed - ${response.info}`);
                }
            })
            .catch((error) => {
                setErrorMessage(error.toString());
                console.log(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    // watchers
    useEffect(() => {
        if (errorMessage && errorMessage.length > 2) toast.error(errorMessage);
    }, [errorMessage]);

    // render
    return (
        <Card>
            <Stack gap={3}>
                <Stack direction="row" justify="center">
                    <LoginLogo />
                </Stack>

                <form onSubmit={DoSet}>
                    <Stack gap={2}>
                        <TextField
                            error={!password1}
                            helperText={!password1 && "Password is required"}
                            fullWidth
                            onChange={(e) => setPassword1(e.target.value)}
                            label={
                                <>
                                    <FontAwesomeIcon icon={faLock} /> Password
                                </>
                            }
                            type={showPassword1 ? "text" : "password"}
                            endAdornment={
                                <IconButton
                                    edgeEnd
                                    aria-label={
                                        showPassword1
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                    data-testid="toggle-password1-visibility"
                                    onClick={() =>
                                        setShowPassword1((prev) => !prev)
                                    }
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    {showPassword1 ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            }
                        />

                        <TextField
                            error={!password2}
                            helperText={!password2 && "Password is required"}
                            fullWidth
                            onChange={(e) => setPassword2(e.target.value)}
                            label={
                                <>
                                    <FontAwesomeIcon icon={faLock} /> Re Enter
                                    Password
                                </>
                            }
                            type={showPassword2 ? "text" : "password"}
                            endAdornment={
                                <IconButton
                                    edgeEnd
                                    aria-label={
                                        showPassword2
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                    data-testid="toggle-password2-visibility"
                                    onClick={() =>
                                        setShowPassword2((prev) => !prev)
                                    }
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    {showPassword2 ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            }
                        />

                        <Button
                            type="submit"
                            className={styles.submitButton}
                            disabled={loading}
                        >
                            Reset Password&nbsp;
                            {loading ? (
                                <CircularProgress size={18} />
                            ) : (
                                <FontAwesomeIcon icon={faRightToBracket} />
                            )}
                        </Button>
                    </Stack>
                </form>
            </Stack>
            <Stack align="center" fullWidth mt={3}>
                <Link to="/login" className={styles.link}>
                    Back to Login
                </Link>
            </Stack>
        </Card>
    );
}
